import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldOff, UserX, WifiOff } from 'lucide-react';
import { useAuth } from '@/shared/auth/contexte';
import { FormulaireIdentite } from '@/shared/comptes/Identite';
import { identiteManquante } from '@/shared/comptes/photo';
import type { Acces } from '@/shared/auth/types';
import { accesDe, voitCommeCeo } from '@/shared/auth/droits';
import { Button, Card, Chargement, EtatVide } from '@/shared/ui';
import { accueilPour } from './navigation';

function Plein({ children }: { children: ReactNode }) {
  return <div className="grid min-h-full place-items-center p-6">{children}</div>;
}

/**
 * Laisse passer une session ouverte sur un compte actif, et dit clairement
 * pourquoi dans tous les autres cas.
 */
export function ExigerSession({ children }: { children: ReactNode }) {
  const { etat, service, relire } = useAuth();
  const { pathname, search } = useLocation();

  /* Sans réseau ni copie du profil : on relit dès que le réseau revient. */
  useEffect(() => {
    if (etat.statut !== 'injoignable') return;
    const relire = () => {
      window.location.reload();
    };
    window.addEventListener('online', relire);
    return () => {
      window.removeEventListener('online', relire);
    };
  }, [etat.statut]);

  if (etat.statut === 'chargement') return <Chargement />;
  if (etat.statut === 'anonyme') {
    const retour = pathname === '/' ? '' : `?retour=${encodeURIComponent(pathname + search)}`;
    return <Navigate to={`/connexion${retour}`} replace />;
  }
  if (etat.statut === 'injoignable') {
    return (
      <Plein>
        <Card className="max-w-md">
          <EtatVide icone={WifiOff} titre="Pas de réseau">
            <p>
              Votre session ne peut pas s’ouvrir sans réseau cette fois-ci. Les photos et les gestes
              en attente restent dans le téléphone : l’appli reprendra seule dès que le réseau
              reviendra.
            </p>
            <Button
              className="mt-4"
              onClick={() => {
                window.location.reload();
              }}
            >
              Réessayer
            </Button>
          </EtatVide>
        </Card>
      </Plein>
    );
  }
  if (etat.statut === 'sans-profil') {
    return (
      <Plein>
        <Card className="max-w-md">
          <EtatVide icone={UserX} titre="Compte sans profil">
            <p>
              Le compte {etat.email} existe, mais aucun profil ne lui est associé. Le bureau doit
              exécuter le script <code>db/001-socle.sql</code> ou recréer l’invitation.
            </p>
            <Button className="mt-4" onClick={() => void service.deconnexion()}>
              Se déconnecter
            </Button>
          </EtatVide>
        </Card>
      </Plein>
    );
  }
  if (!etat.utilisateur.actif) {
    return (
      <Plein>
        <Card className="max-w-md">
          <EtatVide icone={ShieldOff} titre="Compte désactivé">
            <p>Votre accès a été suspendu. Contactez le bureau de VLA Énergie pour le rétablir.</p>
            <Button className="mt-4" onClick={() => void service.deconnexion()}>
              Se déconnecter
            </Button>
          </EtatVide>
        </Card>
      </Plein>
    );
  }
  /* Un compte sans nom signerait ses actions de son adresse e-mail : il le
     saisit avant d'entrer, une fois pour toutes. */
  if (identiteManquante(etat.utilisateur)) {
    return (
      <Plein>
        <Card className="w-full max-w-md">
          <h1 className="font-display text-xl font-semibold">Bienvenue sur VLA Logistique</h1>
          <p className="mt-1 mb-5 text-[13px] text-muted">
            Avant de commencer : votre prénom et votre nom. Ils signent vos actions dans
            l’application.
          </p>
          <FormulaireIdentite compte={etat.utilisateur} libelleAction="Continuer" onFait={relire} />
        </Card>
      </Plein>
    );
  }
  return children;
}

/** Réserve un écran à un rôle (ou au CEO seul) ; les autres retournent à leur accueil. */
export function ExigerRole({
  roles,
  ceoSeul,
  children,
}: {
  roles: readonly Acces[];
  ceoSeul?: boolean | undefined;
  children: ReactNode;
}) {
  const { etat } = useAuth();
  if (etat.statut !== 'connecte') return null;
  const acces = accesDe(etat.utilisateur);
  const ceo = voitCommeCeo(etat.utilisateur);
  if (!roles.includes(acces) || (ceoSeul === true && !ceo)) {
    return <Navigate to={accueilPour(acces, ceo)} replace />;
  }
  return children;
}

export function VersAccueil() {
  const { etat } = useAuth();
  if (etat.statut !== 'connecte') return null;
  return (
    <Navigate to={accueilPour(accesDe(etat.utilisateur), voitCommeCeo(etat.utilisateur))} replace />
  );
}
